import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Phone, Check } from "lucide-react";
import { joursDisponibles } from "../utils/creneaux";
import ChampAntiRobot from "./ChampAntiRobot";
import { trackFormSubmit } from "../utils/analytics";
import { attributionCourante } from "../utils/attribution";

/**
 * Demande de rappel avec choix du créneau.
 *
 * Le public du site est âgé : peu de champs, cibles larges, et un choix de
 * créneau à deux clics plutôt qu'un sélecteur de date. Seuls le nom, le
 * téléphone et le créneau sont demandés — l'e-mail n'est pas nécessaire pour
 * rappeler quelqu'un, et chaque champ en plus fait perdre des demandes.
 */
const RappelModal = ({ isOpen, onClose }) => {
  const jours = useMemo(() => (isOpen ? joursDisponibles() : []), [isOpen]);

  const [jourChoisi, setJourChoisi] = useState(null);
  const [creneauChoisi, setCreneauChoisi] = useState(null);
  const [nom, setNom] = useState("");
  const [telephone, setTelephone] = useState("");
  const [sujet, setSujet] = useState("");
  const [piege, setPiege] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [retour, setRetour] = useState(null);

  const boiteRef = useRef(null);
  const premierChampRef = useRef(null);

  // Sélection par défaut : le premier créneau disponible, pour que le
  // formulaire soit envoyable sans avoir rien à choisir.
  useEffect(() => {
    if (!isOpen || !jours.length) return;
    setJourChoisi(jours[0].cle);
    setCreneauChoisi(jours[0].creneaux[0].id);
    setRetour(null);
    const t = setTimeout(() => premierChampRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen, jours]);

  // Échap ferme, et le focus reste dans la fenêtre tant qu'elle est ouverte.
  useEffect(() => {
    if (!isOpen) return;
    const auClavier = (e) => {
      if (e.key === "Escape") return onClose();
      if (e.key !== "Tab") return;
      const focusables = boiteRef.current?.querySelectorAll(
        'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables?.length) return;
      const premier = focusables[0];
      const dernier = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && document.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    };
    document.addEventListener("keydown", auClavier);
    return () => document.removeEventListener("keydown", auClavier);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const jourCourant = jours.find((j) => j.cle === jourChoisi) || jours[0];

  const envoyer = async (e) => {
    e.preventDefault();
    setRetour(null);

    // Même normalisation que nettoyer_telephone() côté backend : « 0033 6… »
    // et « +33 (0)6… » sont des écritures courantes qui échouaient au contrôle.
    const telNettoye = telephone
      .replace(/[\s.\-()]/g, "")
      .replace(/^0033/, "+33")
      .replace(/^\+330/, "+33");
    if (nom.trim().length < 2) {
      return setRetour({ type: "erreur", message: "Merci d'indiquer votre nom." });
    }
    if (!/^(?:\+33|0)[1-9]\d{8}$/.test(telNettoye)) {
      return setRetour({
        type: "erreur",
        message: "Le numéro de téléphone ne semble pas valide (10 chiffres).",
      });
    }
    if (!jourCourant || !creneauChoisi) {
      return setRetour({ type: "erreur", message: "Merci de choisir un créneau." });
    }

    setEnvoi(true);
    try {
      const creneau = jourCourant.creneaux.find((c) => c.id === creneauChoisi);
      const reponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/send-rappel`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attribution: attributionCourante(),
            nom: nom.trim(),
            telephone: telephone.trim(),
            jour: jourCourant.cle,
            jour_libelle: jourCourant.libelleLong,
            creneau: creneau ? creneau.libelle : "",
            sujet: sujet.trim(),
            website: piege,
          }),
        }
      );
      const resultat = await reponse.json();
      if (resultat.success) {
        trackFormSubmit("rappel");
        setRetour({ type: "succes", message: resultat.message });
      } else {
        setRetour({
          type: "erreur",
          message:
            resultat.message ||
            "L'envoi a échoué. Vous pouvez nous appeler au 09 39 03 54 44.",
        });
      }
    } catch (err) {
      setRetour({
        type: "erreur",
        message:
          "Connexion impossible. Vous pouvez nous appeler au 09 39 03 54 44.",
      });
    }
    setEnvoi(false);
  };

  const classeChoix = (actif) =>
    `min-h-[48px] px-4 py-3 rounded-lg border-2 text-base font-medium transition-colors ${
      actif
        ? "border-orange-700 bg-orange-700 text-white"
        : "border-gray-300 bg-white text-gray-900 hover:border-orange-700 hover:bg-orange-50"
    }`;

  // Portail vers <body> : la fenêtre est posée dans des sections qui portent
  // « relative z-10 », ce qui enferme son z-index dans leur contexte
  // d'empilement — l'en-tête « sticky z-50 » repassait alors devant elle et
  // interceptait le clic sur la croix de fermeture.
  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={boiteRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-rappel"
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-start p-6 border-b border-gray-300">
          <div>
            <h2 id="titre-rappel" className="text-xl font-bold text-gray-900">
              Être rappelé gratuitement
            </h2>
            <p className="text-sm text-gray-700 mt-1">
              Choisissez le moment qui vous arrange, nous vous appelons.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer la fenêtre"
            className="text-gray-500 hover:text-gray-900 p-2 hover:bg-gray-100 rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {retour?.type === "succes" ? (
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
              <Check className="h-7 w-7 text-green-700" aria-hidden="true" />
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-2">
              C&apos;est noté.
            </p>
            <p className="text-gray-700">{retour.message}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 min-h-[48px] px-6 py-3 rounded-lg bg-orange-700 hover:bg-orange-800 text-white font-medium"
            >
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={envoyer} className="p-6 space-y-6">
            <ChampAntiRobot
              id="website-rappel"
              valeur={piege}
              onChange={(e) => setPiege(e.target.value)}
            />
            {retour?.type === "erreur" && (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
              >
                {retour.message}
              </p>
            )}

            <div>
              <label
                htmlFor="rappel-nom"
                className="block text-base font-medium text-gray-900 mb-2"
              >
                Votre nom
              </label>
              <input
                ref={premierChampRef}
                id="rappel-nom"
                name="nom"
                type="text"
                autoComplete="name"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                required
                className="w-full min-h-[48px] px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-orange-700 focus:ring-2 focus:ring-orange-500 focus:outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="rappel-telephone"
                className="block text-base font-medium text-gray-900 mb-2"
              >
                Votre téléphone
              </label>
              <input
                id="rappel-telephone"
                name="telephone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="06 12 34 56 78"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                required
                className="w-full min-h-[48px] px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-orange-700 focus:ring-2 focus:ring-orange-500 focus:outline-none"
              />
            </div>

            {jours.length > 0 ? (
              <>
                <fieldset>
                  <legend className="block text-base font-medium text-gray-900 mb-2">
                    Quel jour ?
                  </legend>
                  <div className="flex flex-wrap gap-2">
                    {jours.map((j) => (
                      <button
                        key={j.cle}
                        type="button"
                        aria-pressed={j.cle === jourChoisi}
                        onClick={() => {
                          setJourChoisi(j.cle);
                          setCreneauChoisi(j.creneaux[0].id);
                        }}
                        className={classeChoix(j.cle === jourChoisi)}
                      >
                        {j.libelle}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="block text-base font-medium text-gray-900 mb-2">
                    À quelle heure ?
                  </legend>
                  <div className="flex flex-wrap gap-2">
                    {jourCourant.creneaux.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        aria-pressed={c.id === creneauChoisi}
                        onClick={() => setCreneauChoisi(c.id)}
                        className={classeChoix(c.id === creneauChoisi)}
                      >
                        {c.libelle}
                      </button>
                    ))}
                  </div>
                </fieldset>
              </>
            ) : (
              <p className="text-gray-700">
                Nos lignes sont fermées pour le moment. Laissez vos coordonnées,
                nous vous rappellerons à la réouverture.
              </p>
            )}

            <div>
              <label
                htmlFor="rappel-sujet"
                className="block text-base font-medium text-gray-900 mb-2"
              >
                Votre projet{" "}
                <span className="font-normal text-gray-600">(facultatif)</span>
              </label>
              <textarea
                id="rappel-sujet"
                name="sujet"
                rows="3"
                value={sujet}
                onChange={(e) => setSujet(e.target.value)}
                placeholder="Douche à remplacer, monte-escalier, question sur l'aide..."
                className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-orange-700 focus:ring-2 focus:ring-orange-500 focus:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={envoi}
              className="w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-lg bg-orange-700 hover:bg-orange-800 disabled:opacity-70 px-6 py-3 text-lg font-semibold text-white shadow-lg"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              {envoi ? "Envoi en cours…" : "Demander à être rappelé"}
            </button>

            <p className="text-xs text-gray-700 text-center">
              Vos coordonnées servent uniquement à vous rappeler. Voir notre{" "}
              <a
                href="/politique-confidentialite"
                className="text-orange-700 underline hover:text-orange-800"
              >
                politique de confidentialité
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

export default RappelModal;
